import React from 'react'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import styled from 'styled-components'
import Icon from '../components/Icon'
import { useInspirationDetail } from '../store/hooks/uiHooks'
import { ANIMATION_SCALE } from '../style'

const CloseButton = styled.button.attrs({
  children: <Icon name="remove" size={15} />
})`
  user-style: none;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #333;
  transition: background-color ${0.3 * ANIMATION_SCALE}s;

  &:hover {
    background: #cf0000;
  }

  &:active {
    background: #cf0000;
    transition-duration: 0s;
  }
`

const ModuleDetailContainer = styled.div.attrs({
  'data-cs-ignore-close': 'moduleDetail'
})`
  background: #fff;
  width: 100%;
  max-width: 500px;
  padding: 40px 35px;
  margin: 0 auto;
  user-select: text;
  position: relative;
  border-radius: 2px;
  overflow: auto;

  ${CloseButton} {
    position: absolute;
    top: 15px;
    right: 15px;
  }
`

const Wrap = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 2;

  ${ModuleDetailContainer} {
    pointer-events: none;
  }

  &.cs-enter,
  &.cs-appear {
    ${ModuleDetailContainer} {
      opacity: 0;
      transform: translateY(30%);
    }
  }

  &.cs-enter-active,
  &.cs-appear-active {
    ${ModuleDetailContainer} {
      transition-duration: ${0.5 * ANIMATION_SCALE}s;
      transition-property: opacity, transform;
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.cs-exit {
    z-index: 1;
    opacity: 1;
    transform: translateY(0);
  }

  &.cs-exit-active {
    transition-duration: ${0.5 * ANIMATION_SCALE}s;
    transition-property: opacity, transform;
    opacity: 0;
    transform: translateY(30%);
  }

  &.cs-enter-done,
  &.cs-appear-done {
    ${ModuleDetailContainer} {
      pointer-events: all;
    }
  }
`

const ModuleHeaderImage = styled.div<{ src?: string }>`
  margin: -40px -35px;
  margin-bottom: 40px;
  font-size: 0;
  padding-bottom: 56.25%;
  background: center/contain no-repeat #f2f2f2 url('${(props) => props.src}');
`

const ModuleTitle = styled.h3`
  font-size: 30px;
  text-transform: uppercase;
`

const ModuleContent = styled.p`
  margin-top: 5px;
  margin-bottom: 0;
`

const ReadMoreLink = styled.a.attrs({
  className: 'button button--solid',
  target: '_blank'
})`
  margin-bottom: 0;
  margin-top: 20px;
  user-select: none;
`

const ModuleDetail: React.FC = () => {
  const { detailModule, closeDetail } = useInspirationDetail()

  return (
    <TransitionGroup>
      {!!detailModule && (
        <CSSTransition classNames="cs" timeout={500 * ANIMATION_SCALE} key={detailModule.id}>
          <Wrap>
            <ModuleDetailContainer>
              <ModuleHeaderImage src={(detailModule.heroImage ?? detailModule.thumbnail ?? detailModule.preview).url} />
              <ModuleTitle>{detailModule?.title}</ModuleTitle>
              <ModuleContent>{detailModule?.description}</ModuleContent>
              {!!detailModule.readMore && (
                <ReadMoreLink href={detailModule.readMore.url}>{detailModule.readMore.label}</ReadMoreLink>
              )}
              <CloseButton onClick={closeDetail} />
            </ModuleDetailContainer>
          </Wrap>
        </CSSTransition>
      )}
    </TransitionGroup>
  )
}

export default ModuleDetail
